import type { Candidate } from '@axiom/validation';
import { PluralizeUtil, TaxonomyUtil } from '@axiom/ui';
import { CandidatesConst } from '@axiom/const';

const {
  ProfileStatuses: {
    Beach,
    Waitlist,
    Alum,
    AlumDNR,
    Idle,
    PendingActive,
    Active,
    PendingBeach,
  },
} = CandidatesConst;

export const PreferredWeeklyHoursUtil = {
  isOnLeave({ candidate }: { candidate: Candidate }) {
    return (candidate.maxHours ?? 0) === 0;
  },

  getMaxWeeklyHours({ candidate }: { candidate: Candidate }) {
    return candidate.preferredWeeklyHoursMaximum ?? candidate.maxHours ?? 0;
  },

  getHoursString({ hours }: { hours: number }) {
    return `${hours} ${PluralizeUtil({ text: 'hour', count: hours })}`;
  },

  getRemainingReservedHours({ candidate }: { candidate: Candidate }) {
    const desiredHours =
      candidate.desiredWeeklyMaxHours ??
      candidate.preferredWeeklyHoursMaximum ??
      0;
    const reservedHours =
      candidate.calculatedEngagedAndReservedHours?.totalReservedHours ?? 0;

    return desiredHours - reservedHours;
  },

  getStatusMessage({ candidate }: { candidate: Candidate }) {
    const statusStarter = 'You are currently';
    const { profileStatus } = candidate;
    // Accumulated rather than returned per case: an explicit `return undefined` for the
    // no-banner statuses gets stripped by lint autofix, which then trips noImplicitReturns.
    let text;

    switch (profileStatus) {
      case Beach:
      case Waitlist: {
        text = `${statusStarter} on the ${profileStatus.toLowerCase()}.`;
        break;
      }

      case AlumDNR:
      case Alum: {
        text = `${statusStarter} an Axiom alum.`;
        break;
      }

      case Idle: {
        text = `${statusStarter} not looking for work with Axiom.`;
        break;
      }

      case PendingActive:
      case Active:
      case PendingBeach: {
        const remainingReservedHours =
          PreferredWeeklyHoursUtil.getRemainingReservedHours({ candidate });

        if (remainingReservedHours > 0) {
          text = `You're still looking for ${PreferredWeeklyHoursUtil.getHoursString({ hours: remainingReservedHours })} of work / week.`;
        } else if (TaxonomyUtil.isLawyer(candidate.businessTitle)) {
          text =
            "You're fully engaged. If you would like additional work, please speak to your Talent Partner.";
        } else {
          text = "You're fully engaged.";
        }
        break;
      }

      default:
    }

    return text;
  },
};
